import { useEffect, useState } from 'react';
import { BrowserInfo, detect } from "detect-browser";
import { memoizeFunction } from '@fluentui/react';
import { Delayed } from './Utils';

/**
 * https://developer.chrome.com/blog/viewport-resize-behavior/
 * @returns true if browser supports 'interactive-widget' value of the <meta type='viewport'> tag.
 */
export const isInteractiveWidgetSupported = memoizeFunction(() => {
    const browserDetect = detect() as BrowserInfo;
    return browserDetect && (browserDetect?.os == 'Android OS' || browserDetect?.name == 'firefox');
});

/**
 * Detect if the browsers visible viewport size. Detect it changes on resize.
 * @returns visible viewport size
 */
export const useVisualViewport = (isMobile: boolean = false) => {
    const [viewportHeight, setViewportHeight] = useState<number|null>(null);
    const onViewportResize = (event: Event) => {
        if (isMobile && !isInteractiveWidgetSupported()) {

            const viewport = event.currentTarget as VisualViewport;
            setViewportHeight(viewport.height);
        }
    }

    useEffect(() => {
        window.visualViewport?.addEventListener("resize", onViewportResize);

        return () => {
            window.visualViewport?.removeEventListener("resize", onViewportResize);
        }
    }, []);

    return { height: viewportHeight };
};

/**
 * Detect if the browsers visible viewport size. Detect it changes on resize.
 * @returns visible viewport size
 */
export const useVisualViewportWithPosition = (isMobile: boolean = false) => {
    const [viewportHeight, setViewportHeight] = useState<string>(''); //Empty string by default
    const [offsetTop, setOffsetTop] = useState<number|undefined>(undefined);
    const [offsetBottom, setOffsetBottom] = useState<number|undefined>(undefined);
    const isInteractiveWidgetNotSupported = !isInteractiveWidgetSupported();

    type ResizeArgs =  {
        offsetTop?: number,
        height: string,
        offsetBottom?: number
    };

    useEffect(() => {
        const delayed = new Delayed();

        const getArgs = (viewport:VisualViewport): ResizeArgs => ({
            offsetTop: viewport.offsetTop,
            height: `${Math.ceil(viewport.height)}px`,
        });

        /** Updates state of the viewport (triggers re-render). */
        const onViewportResize = (eventArgs: ResizeArgs) => {
            if (isMobile && isInteractiveWidgetNotSupported) {
                // console.log(eventArgs.height, eventArgs.offsetBottom, eventArgs.offsetTop);
                setViewportHeight(eventArgs.height);
                setOffsetBottom(eventArgs.offsetBottom);
                setOffsetTop(eventArgs.offsetTop);
            }
        }
        const effectTwice = (event: Event) => {
            onViewportResize({
                height: '100dvh',   //'unset' is not used instead, since while keyboard hiding it would show the content of "main-content-container" DOM-element (it looks like a glitch)
                offsetBottom: 0
            });
            const viewport = event.currentTarget as VisualViewport;
            const eventArgs = getArgs(viewport);
            const isChanged = isViewportChanged(eventArgs);

            //Open or closing the on-screen keyboard in iOS does not immediately update the visual viewport
            delayed.requestAnimationFrame(() => {
                onViewportResize(eventArgs);
                if (isChanged) {
                    //Update ContextualMenu position (refresh it's cache):
                    dispatchWindowResize(eventArgs);
                }
            });
        }
        function dispatchWindowResize(eventArgs: ResizeArgs) {
            const resizeEvent = new Event('resize');
            window.dispatchEvent(resizeEvent);    //this sould re-calculate "CalloutContentBase" component position (othwerwise "cachedBounds.current" old value would be used for the compoennt position)
        }
        function isViewportChanged(eventArgs: ResizeArgs) {
            return viewportHeight !== eventArgs.height
                || offsetTop !== eventArgs.offsetTop
                || offsetBottom !== eventArgs.offsetBottom;
        }

        if(isMobile && isInteractiveWidgetNotSupported) {
            window.visualViewport?.addEventListener("resize", effectTwice);
        }

        return () => {
            window.visualViewport?.removeEventListener("resize", effectTwice);
        }
    }, []);
    return { height: viewportHeight, offsetTop: offsetTop, offsetBottom: offsetBottom };
};
